"use client";

import Link from "next/link";

import { resolveTenantFooter } from "@/config/tenant-footer";
import type { TenantIdentity } from "@/config/tenants";
import { useAppLocale } from "@/features/localization/app-locale-provider";

export function TenantFooter({ tenant }: { tenant: TenantIdentity }) {
  const { locale } = useAppLocale();
  const copy = resolveTenantFooter(tenant, locale, new Date().getFullYear());

  const links = [
    { href: `/${tenant.code}/terms`, label: copy.termsOfService },
    { href: `/${tenant.code}/cookies`, label: copy.cookiePolicy },
    { href: `/${tenant.code}/privacy`, label: copy.privacyPolicy },
    ...(tenant.code === "12sc"
      ? [{ href: `/${tenant.code}/refund`, label: copy.refundPolicy }]
      : []),
  ];

  return (
    <footer className="tenant-structure border-t border-white/15 px-4 py-5 text-sm" aria-label={`${tenant.displayName} footer`}>
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center gap-2 text-center sm:flex-row sm:flex-wrap sm:gap-x-2">
        <span>{copy.copyright}</span>
        <nav className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1" aria-label="Footer">
          {links.map((link) => (
            <span className="flex items-center gap-2" key={link.href}>
              <span aria-hidden="true">|</span>
              <Link
                className="underline-offset-4 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                href={link.href}
                rel="noopener noreferrer"
                target="_blank"
              >
                <span className="sr-only">{`${link.label} (${locale === "fr" ? "s’ouvre dans un nouvel onglet" : "opens in a new tab"})`}</span>
                <span aria-hidden="true">{link.label}</span>
              </Link>
            </span>
          ))}
        </nav>
      </div>
    </footer>
  );
}
